import React, { createContext, useContext, useState, useCallback } from 'react';

const MapControlsContext = createContext();

export const useMapControls = () => {
  const context = useContext(MapControlsContext);
  if (!context) {
    throw new Error('useMapControls must be used within a MapControlsProvider');
  }
  return context;
};

export const MapControlsProvider = ({ children }) => {
  // Track minimized state for each control by ID
  const [minimizedControls, setMinimizedControls] = useState(new Set());
  
  // Track the order of controls for positioning
  const [controlOrder, setControlOrder] = useState([]);

  const toggleMinimized = useCallback((controlId) => {
    setMinimizedControls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(controlId)) {
        newSet.delete(controlId);
      } else {
        newSet.add(controlId);
      }
      return newSet;
    });
  }, []);

  const minimize = useCallback((controlId) => {
    setMinimizedControls(prev => new Set(prev.add(controlId)));
  }, []);

  const expand = useCallback((controlId) => {
    setMinimizedControls(prev => {
      const newSet = new Set(prev);
      newSet.delete(controlId);
      return newSet;
    });
  }, []);

  const isMinimized = useCallback((controlId) => {
    return minimizedControls.has(controlId);
  }, [minimizedControls]);

  const registerControl = useCallback((controlId) => {
    setControlOrder(prev => {
      if (!prev.includes(controlId)) {
        return [...prev, controlId];
      }
      return prev;
    });
  }, []);

  const unregisterControl = useCallback((controlId) => {
    setControlOrder(prev => prev.filter(id => id !== controlId));
    setMinimizedControls(prev => {
      const newSet = new Set(prev);
      newSet.delete(controlId);
      return newSet;
    });
  }, []);

  const value = {
    minimizedControls,
    controlOrder,
    toggleMinimized,
    minimize,
    expand,
    isMinimized,
    registerControl,
    unregisterControl,
  };

  return (
    <MapControlsContext.Provider value={value}>
      {children}
    </MapControlsContext.Provider>
  );
};

export default MapControlsContext;